/** HELM run history (A7Y-3870/A7Y-3872) — each run's Madrid-local day, trigger,
 * outcome (reusing the Autobuild console's `OutcomeBadge` rather than a
 * second outcome-chip component, plan §8 DRY), critical count and nudge
 * status. `localDate` is server-computed (D20) rather than formatted from
 * `startedAt` in the browser, so a viewer outside Madrid never files a
 * late-evening run under the wrong day.
 *
 * A succeeded run is clickable — it opens that run read-only via
 * `onOpenRun` (D14) — a running/failed/abandoned one is not, since it has no
 * viewable facts/cards.
 *
 * A single Catalyst `Table` (A7Y-4407 round 3) rather than a hand-rolled
 * dual stacked-list/table rendering — `Table`'s own wrapper already scrolls
 * horizontally rather than overflowing the page, which is an accepted
 * tradeoff for real aligned columns on a narrow phone.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type { HelmRunSummary } from '@a7y/platform-health/helm';
import { OutcomeBadge } from '@/pages/autobuild/OutcomeBadge';
import { toAutobuildOutcome } from './helm-outcome';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';

const DISPLAY_LIMIT = 30;

export function RunHistory({
  runs,
  selectedRunId,
  onOpenRun,
}: {
  runs: HelmRunSummary[];
  selectedRunId: string | null;
  onOpenRun: (runId: string) => void;
}) {
  const visible = runs.slice(0, DISPLAY_LIMIT);

  return (
    <HelmSection title="Run history" lineOn="none">
      {visible.length === 0 ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <Table dense data-testid="helm-run-history-table" className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Started</TableHeader>
              <TableHeader>Trigger</TableHeader>
              <TableHeader>Outcome</TableHeader>
              <TableHeader>Critical</TableHeader>
              <TableHeader>Nudge</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((run) => (
              <TableRow
                key={run.runId}
                data-testid="helm-run-row"
                data-selected={run.runId === selectedRunId}
              >
                <TableCell>
                  {run.status === 'succeeded' ? (
                    <button
                      type="button"
                      data-testid="helm-run-open"
                      onClick={() => onOpenRun(run.runId)}
                      className="min-h-11 hover:underline"
                    >
                      {run.localDate}
                    </button>
                  ) : (
                    run.localDate
                  )}
                </TableCell>
                <TableCell className="capitalize">{run.trigger}</TableCell>
                <TableCell>
                  <OutcomeBadge outcome={toAutobuildOutcome(run.status)} />
                </TableCell>
                <TableCell>{run.criticalCount ?? '—'}</TableCell>
                <TableCell className="capitalize">{run.nudge ?? 'no nudge'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}
